import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import "./EnterpriseSection.css";

export const CorporateIcon = ({ size, color = "white" }) => {
  const src =
    color === "black"
      ? "/Image/Hero/corporate-grey.svg"
      : "/Image/Hero/corporate.svg";
  return <img src={src} alt="corporate" width={size} height={size} />;
};

export const GradIcon = ({ size, color = "white" }) => {
  const src =
    color === "black"
      ? "/Image/Hero/Courses-grey.svg"
      : "/Image/Hero/Courses.svg";
  return <img src={src} alt="graduation" width={size} height={size} />;
};

export const StartupIcon = ({ size, color = "white" }) => {
  const src =
    color === "black"
      ? "/Image/Hero/Startups-grey.svg"
      : "/Image/Hero/Startups.svg";
  return <img src={src} alt="startup" width={size} height={size} />;
};

export const TickIcon = () => (
  <div
    style={{
      width: 22,
      height: 22,
      borderRadius: "50%",
      backgroundColor: "#2DC7FD",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    aria-hidden="true"
  >
    <img src="/Image/Hero/Tick2.svg" width="14" height="14" alt="" />
  </div>
);

export default function EnterpriseSection() {
  const { t } = useTranslation();
  const history = useHistory();

  const [active, setActive] = useState("corporates");
  const [direction, setDirection] = useState("right");

  // 🔹 same tab direction logic
  const tabOrder = {
    corporates: 0,
    universities: 1,
    startups: 2,
  };

  const handleTabChange = (nextKey) => {
    if (nextKey === active) return;
    setDirection(
      tabOrder[nextKey] > tabOrder[active] ? "right" : "left"
    );
    setActive(nextKey);
  };

  // 🔹 shared video logic (same as CareerSection)
  const videoRef = useRef(null);
  const [showThumbnail, setShowThumbnail] = useState(true);

  const enterpriseVideoUrl =
    "https://stage-nxnearby-assets.s3.ap-south-1.amazonaws.com/Enterprise%27s%20video.m4v";

  const enterpriseThumbnail =
    "/Image/Hero/enterprise-thumb.jpg"; // change if needed

  const handlePlay = () => {
    setShowThumbnail(false);
    requestAnimationFrame(() => {
      videoRef.current?.play();
    });
  };

  const handleVideoEnded = () => {
    setShowThumbnail(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const tabs = [
    {
      key: "corporates",
      label: t("home.enterprise.tabs.corporates"),
      title: t("home.enterprise.corporates.title"),
      desc: t("home.enterprise.corporates.desc"),
      benefits: [
        t("home.enterprise.corporates.benefits.0"),
        t("home.enterprise.corporates.benefits.1"),
        t("home.enterprise.corporates.benefits.2"),
        t("home.enterprise.corporates.benefits.3"),
      ],
    },
    {
      key: "universities",
      label: t("home.enterprise.tabs.universities"),
      title: t("home.enterprise.universities.title"),
      desc: t("home.enterprise.universities.desc"),
      benefits: [
        t("home.enterprise.universities.benefits.0"),
        t("home.enterprise.universities.benefits.1"),
        t("home.enterprise.universities.benefits.2"),
        t("home.enterprise.universities.benefits.3"),
      ],
    },
    {
      key: "startups",
      label: t("home.enterprise.tabs.startups"),
      title: t("home.enterprise.startups.title"),
      desc: t("home.enterprise.startups.desc"),
      benefits: [
        t("home.enterprise.startups.benefits.0"),
        t("home.enterprise.startups.benefits.1"),
        t("home.enterprise.startups.benefits.2"),
        t("home.enterprise.startups.benefits.3"),
      ],
    },
  ];

  const IconForKey = {
    corporates: CorporateIcon,
    universities: GradIcon,
    startups: StartupIcon,
  };

  const activeTab = tabs.find((t) => t.key === active);

  const handleLearnMore = () => {
    history.push({
      pathname: "/signup",
      state: { detail: "LOCAL BUSINESS" },
    });
  };

  return (
    <section className="enterprise-section">
      <div className="enterprise-inner">
        <header className="enterprise-header">
          <h2 className="enterprise-title">
            {t("home.enterprise.header.titlePart1")}{" "}
            <span className="accent">
              {t("home.enterprise.header.accent")}
            </span>
          </h2>
          <p className="enterprise-sub">
            {t("home.enterprise.header.sub")}
          </p>
        </header>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <nav className="enterprise-tabs">
            {tabs.map((tab) => {
              const Icon = IconForKey[tab.key];
              const isActive = active === tab.key;

              return (
                <button
                  key={tab.key}
                  className={`ent-tab ${isActive ? "active" : ""}`}
                  onClick={() => handleTabChange(tab.key)}
                >
                  <span className="tab-ico-box">
                    <Icon size={22} color={isActive ? "white" : "black"} />
                  </span>
                  <span className="tab-label">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="enterprise-main">
          <aside className="enterprise-info">
            <div
              key={active}
              className={`enterprise-info-panel ${
                direction === "right"
                  ? "slide-in-right"
                  : "slide-in-left"
              }`}
            >
              <h3>{activeTab.title}</h3>
              <p className="enterprise-info-desc">{activeTab.desc}</p>

              <ul className="enterprise-benefits">
                {activeTab.benefits.map((b, i) => (
                  <li key={i}>
                    <TickIcon />
                    {b}
                  </li>
                ))}
              </ul>

              <button className="btn-primary" onClick={handleLearnMore}>
                {t("home.enterprise.learnMore", "Learn More")}
              </button>
            </div>
          </aside>

          {/* 🔹 video area */}
          <div className="enterprise-media">
            <div className="video-placeholder">
              <div className="video-layer">
                {showThumbnail && (
                  <div
                    className="video-thumb"
                    role="button"
                    tabIndex={0}
                    onClick={handlePlay}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handlePlay()
                    }
                  >
                    <img
                      src={enterpriseThumbnail}
                      alt="Enterprise video preview"
                    />
                    <div className="video-overlay" />

                    <div className="play-btn" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                )}

                <video
                  ref={videoRef}
                  className="video-element"
                  src={enterpriseVideoUrl}
                  controls
                  playsInline
                  preload="metadata"
                  onEnded={handleVideoEnded}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
